import { Button } from "#components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "#components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "#components/ui/popover"
import { useIsAuthenticated } from "#hooks/use-auth"
import { useI18n } from "#hooks/use-i18n"
import { useUpdateUserCurrency, useUserCurrency } from "#hooks/use-user-settings"
import { cn } from "#lib/utils"
import { Check, ChevronsUpDown, DollarSign } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

// Comprehensive list of ISO 4217 currency codes with their symbols
export const CURRENCIES = [
  // Major currencies
  { code: "USD", name: "US Dollar", symbol: "$", region: "Americas" },
  { code: "EUR", name: "Euro", symbol: "€", region: "Europe" },
  { code: "GBP", name: "British Pound", symbol: "£", region: "Europe" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥", region: "Asia" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥", region: "Asia" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$", region: "Oceania" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$", region: "Americas" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF", region: "Europe" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$", region: "Asia" },
  { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$", region: "Oceania" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr", region: "Europe" },
  { code: "NOK", name: "Norwegian Krone", symbol: "kr", region: "Europe" },
  { code: "DKK", name: "Danish Krone", symbol: "kr", region: "Europe" },
  { code: "PLN", name: "Polish Złoty", symbol: "zł", region: "Europe" },
  { code: "RUB", name: "Russian Ruble", symbol: "₽", region: "Europe" },
  { code: "INR", name: "Indian Rupee", symbol: "₹", region: "Asia" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$", region: "Americas" },
  { code: "ZAR", name: "South African Rand", symbol: "R", region: "Africa" },
  { code: "KRW", name: "South Korean Won", symbol: "₩", region: "Asia" },
  { code: "MXN", name: "Mexican Peso", symbol: "$", region: "Americas" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$", region: "Asia" },
  { code: "THB", name: "Thai Baht", symbol: "฿", region: "Asia" },
  { code: "TRY", name: "Turkish Lira", symbol: "₺", region: "Asia" },
  { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp", region: "Asia" },
  { code: "MYR", name: "Malaysian Ringgit", symbol: "RM", region: "Asia" },
  { code: "PHP", name: "Philippine Peso", symbol: "₱", region: "Asia" },
  { code: "CZK", name: "Czech Koruna", symbol: "Kč", region: "Europe" },
  { code: "HUF", name: "Hungarian Forint", symbol: "Ft", region: "Europe" },
  { code: "RON", name: "Romanian Leu", symbol: "lei", region: "Europe" },
  { code: "ILS", name: "Israeli Shekel", symbol: "₪", region: "Asia" },
  { code: "CLP", name: "Chilean Peso", symbol: "$", region: "Americas" },
  { code: "ARS", name: "Argentine Peso", symbol: "$", region: "Americas" },
  { code: "COP", name: "Colombian Peso", symbol: "$", region: "Americas" },
  { code: "PEN", name: "Peruvian Sol", symbol: "S/", region: "Americas" },
  { code: "VND", name: "Vietnamese Dong", symbol: "₫", region: "Asia" },
  { code: "TWD", name: "Taiwan Dollar", symbol: "NT$", region: "Asia" },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ", region: "Middle East" },
  { code: "SAR", name: "Saudi Riyal", symbol: "﷼", region: "Middle East" },
  { code: "QAR", name: "Qatari Riyal", symbol: "﷼", region: "Middle East" },
  { code: "KWD", name: "Kuwaiti Dinar", symbol: "د.ك", region: "Middle East" },
  { code: "BHD", name: "Bahraini Dinar", symbol: ".د.ب", region: "Middle East" },
  { code: "OMR", name: "Omani Rial", symbol: "﷼", region: "Middle East" },
  { code: "JOD", name: "Jordanian Dinar", symbol: "د.ا", region: "Middle East" },
  { code: "LBP", name: "Lebanese Pound", symbol: "£", region: "Middle East" },
  { code: "EGP", name: "Egyptian Pound", symbol: "£", region: "Africa" },
  { code: "NGN", name: "Nigerian Naira", symbol: "₦", region: "Africa" },
  { code: "GHS", name: "Ghanaian Cedi", symbol: "₵", region: "Africa" },
  { code: "KES", name: "Kenyan Shilling", symbol: "KSh", region: "Africa" },
  { code: "UGX", name: "Ugandan Shilling", symbol: "USh", region: "Africa" },
  { code: "TZS", name: "Tanzanian Shilling", symbol: "TSh", region: "Africa" },
  { code: "ETB", name: "Ethiopian Birr", symbol: "Br", region: "Africa" },
  { code: "MAD", name: "Moroccan Dirham", symbol: "د.م.", region: "Africa" },
  { code: "DZD", name: "Algerian Dinar", symbol: "د.ج", region: "Africa" },
  { code: "TND", name: "Tunisian Dinar", symbol: "د.ت", region: "Africa" },
  { code: "PKR", name: "Pakistani Rupee", symbol: "₨", region: "Asia" },
  { code: "BDT", name: "Bangladeshi Taka", symbol: "৳", region: "Asia" },
  { code: "LKR", name: "Sri Lankan Rupee", symbol: "₨", region: "Asia" },
  { code: "NPR", name: "Nepalese Rupee", symbol: "₨", region: "Asia" },
  { code: "MMK", name: "Myanmar Kyat", symbol: "K", region: "Asia" },
  { code: "KHR", name: "Cambodian Riel", symbol: "៛", region: "Asia" },
  { code: "LAK", name: "Lao Kip", symbol: "₭", region: "Asia" },
  { code: "BND", name: "Brunei Dollar", symbol: "B$", region: "Asia" },
  { code: "KZT", name: "Kazakhstani Tenge", symbol: "₸", region: "Asia" },
  { code: "UZS", name: "Uzbekistani Som", symbol: "so'm", region: "Asia" },
  { code: "KGS", name: "Kyrgystani Som", symbol: "лв", region: "Asia" },
  { code: "TJS", name: "Tajikistani Somoni", symbol: "SM", region: "Asia" },
  { code: "AFN", name: "Afghan Afghani", symbol: "؋", region: "Asia" },
  { code: "IRR", name: "Iranian Rial", symbol: "﷼", region: "Middle East" },
  { code: "IQD", name: "Iraqi Dinar", symbol: "ع.د", region: "Middle East" },
  { code: "SYP", name: "Syrian Pound", symbol: "£", region: "Middle East" },
  { code: "YER", name: "Yemeni Rial", symbol: "﷼", region: "Middle East" },
  { code: "AMD", name: "Armenian Dram", symbol: "֏", region: "Asia" },
  { code: "AZN", name: "Azerbaijani Manat", symbol: "₼", region: "Asia" },
  { code: "GEL", name: "Georgian Lari", symbol: "₾", region: "Asia" },
  { code: "BYN", name: "Belarusian Ruble", symbol: "Br", region: "Europe" },
  { code: "MDL", name: "Moldovan Leu", symbol: "lei", region: "Europe" },
  { code: "UAH", name: "Ukrainian Hryvnia", symbol: "₴", region: "Europe" },
  { code: "BGN", name: "Bulgarian Lev", symbol: "лв", region: "Europe" },
  { code: "RSD", name: "Serbian Dinar", symbol: "дин.", region: "Europe" },
  { code: "BAM", name: "Bosnia-Herzegovina Mark", symbol: "KM", region: "Europe" },
  { code: "MKD", name: "Macedonian Denar", symbol: "ден", region: "Europe" },
  { code: "ALL", name: "Albanian Lek", symbol: "Lek", region: "Europe" },
  { code: "ISK", name: "Icelandic Króna", symbol: "kr", region: "Europe" },
  { code: "HRK", name: "Croatian Kuna", symbol: "kn", region: "Europe" },
]

// Type for currency
export type Currency = (typeof CURRENCIES)[number]

// Group currencies by region
export const CURRENCIES_BY_REGION = CURRENCIES.reduce(
  (acc, currency) => {
    if (!acc[currency.region]) {
      acc[currency.region] = []
    }
    acc[currency.region].push(currency)
    return acc
  },
  {} as Record<string, Currency[]>
)

export function CurrencySwitcherCompact() {
  const { t } = useI18n()
  const currentCurrency = useUserCurrency()
  const isAuthenticated = useIsAuthenticated()
  const updateCurrency = useUpdateUserCurrency()
  const [open, setOpen] = useState(false)

  const currentCurrencyInfo = useMemo(
    () => CURRENCIES.find((c) => c.code === currentCurrency),
    [currentCurrency]
  )

  const handleCurrencySelect = useCallback(async (value: string) => {
    if (isAuthenticated) {
      // If authenticated, update user settings on server
      await updateCurrency.mutateAsync(value)
    }
    setOpen(false)
  }, [])

  const onFilterChange = useCallback((value: string, query: string) => {
    return value.toLowerCase().includes(query.toLowerCase()) ? 1 : 0
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-fit h-9 justify-between"
        >
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {currentCurrencyInfo ? (
              <span className="flex items-center gap-1.5">
                <span>{currentCurrencyInfo.symbol}</span>
                <span className="hidden sm:inline">{currentCurrencyInfo.code}</span>
                <span className="sm:hidden">{currentCurrencyInfo.code}</span>
              </span>
            ) : (
              currentCurrency
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[250px] p-0"
        align="start"
      >
        <Command filter={onFilterChange}>
          <CommandInput placeholder={t("components.currency-switcher.searchPlaceholder")} />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            {Object.entries(CURRENCIES_BY_REGION).map(([region, currencies]) => (
              <CommandGroup key={region} heading={region}>
                {currencies.map((currency) => (
                  <CommandItem
                    key={`${currency.code} ${currency.name} ${currency.symbol} ${currency.region}`}
                    value={`${currency.code} ${currency.name} ${currency.symbol} ${currency.region}`}
                    onSelect={() => handleCurrencySelect(currency.code)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentCurrency === currency.code ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="flex items-center gap-2">
                      <span>{currency.symbol}</span>
                      <span>{currency.code}</span>
                      <span className="text-muted-foreground text-xs">- {currency.name}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
